if (!customElements.get('pdp-sticky-add-to-cart')) {
  customElements.define(
    'pdp-sticky-add-to-cart',
    class PdpStickyAddToCart extends HTMLElement {
      constructor() {
        super();

        this.sectionId = this.dataset.sectionId;
        this.ctaButton = this.querySelector('[data-sticky-add-to-cart]');
        this.ctaText = this.querySelector('[data-sticky-cta-text]');
        this.scrollTopButton = this.querySelector('[data-sticky-scroll-top]');
        this.stickyPrice = this.querySelector('.pdp-sticky-bar__price');
        this.mainSubmitButton = null;
        this.mainPrice = null;
        this.sentinel = null;
        this.mobileSentinel = null;
        this.mediaWrapper = null;
        this.intersectionObserver = null;
        this.mobileIntersectionObserver = null;
        this.mediaObserver = null;
        this.buttonObserver = null;
        this.variantChangeUnsubscriber = null;
        this.addedTimeout = null;
        this.isSentinelIntersecting = true;
        this.isMobileSentinelIntersecting = true;
        this.isMediaIntersecting = false;
        this.mobileMediaQuery = window.matchMedia('(max-width: 749px)');
        this.onMediaQueryChange = this.updateVisibility.bind(this);
      }

      connectedCallback() {
        this.mainSubmitButton = document.getElementById(`ProductSubmitButton-${this.sectionId}`);
        this.mainPrice = document.getElementById(`price-${this.sectionId}`);
        this.sentinel = this.resolveSentinel();
        this.mobileSentinel = this.resolveMobileSentinel();
        this.mediaWrapper = this.resolveMediaWrapper();

        this.setupIntersectionObserver();
        this.setupMobileIntersectionObserver();
        this.setupMediaObserver();
        this.setupEventListeners();
        this.setupButtonObserver();
        this.syncButtonState();
        this.syncPrice();
        this.bindMediaQueryListener();
        this.setupResizeObserver();
        this.updateVisibility();

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, this.onVariantChange.bind(this));
        }
      }

      disconnectedCallback() {
        this.intersectionObserver?.disconnect();
        this.mobileIntersectionObserver?.disconnect();
        this.mediaObserver?.disconnect();
        this.buttonObserver?.disconnect();
        this.resizeObserver?.disconnect();
        this.variantChangeUnsubscriber?.();
        clearTimeout(this.addedTimeout);
        this.unbindMediaQueryListener();
        document.documentElement.style.removeProperty('--pdp-sticky-bar-offset');
      }

      resolveSentinel() {
        return (
          document.querySelector('.product-description-container') ||
          document.getElementById(`MainProduct-${this.sectionId}`) ||
          this.closest('product-info') ||
          document.querySelector('.list-of-features-outer-container') ||
          document.querySelector('[data-pdp-sticky-sentinel]') ||
          document.querySelector('.add-to-bag-button') ||
          document.querySelector(`#ProductInfo-${this.sectionId}`)
        );
      }

      resolveMediaWrapper() {
        return (
          document.querySelector(`#MainProduct-${this.sectionId} .product__media-wrapper.grid__item`) ||
          document.querySelector('.product__media-wrapper.grid__item')
        );
      }

      resolveMobileSentinel() {
        return (
          document.querySelector(`#MainProduct-${this.sectionId} .quantity-and-button-wrap`) ||
          document.querySelector('.quantity-and-button-wrap')
        );
      }

      setupIntersectionObserver() {
        if (!this.sentinel) return;

        this.intersectionObserver = new IntersectionObserver(
          ([entry]) => {
            this.isSentinelIntersecting = entry.isIntersecting;
            this.updateVisibility();
          },
          { threshold: 0 }
        );

        this.intersectionObserver.observe(this.sentinel);
      }

      setupMediaObserver() {
        if (!this.mediaWrapper) return;

        this.mediaObserver = new IntersectionObserver(
          ([entry]) => {
            this.isMediaIntersecting = entry.isIntersecting;
            this.updateVisibility();
          },
          { threshold: 0 }
        );

        this.mediaObserver.observe(this.mediaWrapper);
      }

      setupMobileIntersectionObserver() {
        if (!this.mobileSentinel) return;

        this.mobileIntersectionObserver = new IntersectionObserver(
          ([entry]) => {
            this.isMobileSentinelIntersecting = entry.isIntersecting;
            this.updateVisibility();
          },
          { threshold: 0 }
        );

        this.mobileIntersectionObserver.observe(this.mobileSentinel);
      }

      bindMediaQueryListener() {
        if (typeof this.mobileMediaQuery.addEventListener === 'function') {
          this.mobileMediaQuery.addEventListener('change', this.onMediaQueryChange);
          return;
        }

        if (typeof this.mobileMediaQuery.addListener === 'function') {
          this.mobileMediaQuery.addListener(this.onMediaQueryChange);
        }
      }

      unbindMediaQueryListener() {
        if (typeof this.mobileMediaQuery.removeEventListener === 'function') {
          this.mobileMediaQuery.removeEventListener('change', this.onMediaQueryChange);
          return;
        }

        if (typeof this.mobileMediaQuery.removeListener === 'function') {
          this.mobileMediaQuery.removeListener(this.onMediaQueryChange);
        }
      }

      updateVisibility() {
        if (this.mobileMediaQuery.matches) {
          const hideByQuantityVisibility = this.isMobileSentinelIntersecting;
          const hideByMediaVisibility = this.isMediaIntersecting;
          const isVisible = !hideByQuantityVisibility && !hideByMediaVisibility;

          this.classList.toggle('is-visible', isVisible);
          this.setAttribute('aria-hidden', !isVisible);
          this.syncFooterOffset(isVisible);
          return;
        }

        const hideByDescriptionVisibility = this.isSentinelIntersecting;
        const hideByMediaVisibility = false;
        const isVisible = !hideByDescriptionVisibility && !hideByMediaVisibility;

        this.classList.toggle('is-visible', isVisible);
        this.setAttribute('aria-hidden', !isVisible);
        this.syncFooterOffset(isVisible);
      }

      setupResizeObserver() {
        if (typeof ResizeObserver !== 'function') return;

        this.resizeObserver = new ResizeObserver(() => {
          this.syncFooterOffset(this.classList.contains('is-visible'));
        });

        this.resizeObserver.observe(this);
      }

      syncFooterOffset(isVisible) {
        document.documentElement.style.setProperty(
          '--pdp-sticky-bar-offset',
          isVisible ? `${this.offsetHeight}px` : '0px'
        );
      }

      setupEventListeners() {
        this.ctaButton?.addEventListener('click', this.onAddToCartClick.bind(this));
        this.scrollTopButton?.addEventListener('click', this.onScrollToTop.bind(this));
      }

      setupButtonObserver() {
        if (!this.mainSubmitButton) return;

        this.buttonObserver = new MutationObserver(() => {
          this.syncButtonState();
        });

        this.buttonObserver.observe(this.mainSubmitButton, {
          attributes: true,
          attributeFilter: ['disabled', 'class'],
          childList: true,
          subtree: true,
        });
      }

      onVariantChange(event) {
        if (event.data.sectionId !== this.sectionId) return;

        const updatedPrice = event.data.html.getElementById(`price-${this.sectionId}`);
        if (updatedPrice && this.stickyPrice) {
          this.stickyPrice.innerHTML = updatedPrice.innerHTML;
        }

        this.syncButtonState();
      }

      syncPrice() {
        if (!this.mainPrice || !this.stickyPrice) return;
        this.stickyPrice.innerHTML = this.mainPrice.innerHTML;
      }

      syncButtonState() {
        if (!this.ctaButton || !this.ctaText || !this.mainSubmitButton) return;

        const mainText = this.mainSubmitButton.querySelector('span:not(.sold-out-message):not(.icon-wrap)');
        const isDisabled = this.mainSubmitButton.hasAttribute('disabled');
        const isAdded = this.mainSubmitButton.classList.contains('added');

        this.ctaButton.disabled = isDisabled;
        this.ctaButton.classList.toggle('is-added', isAdded);

        if (mainText?.textContent) {
          this.ctaText.textContent = mainText.textContent.trim();
        }
      }

      onAddToCartClick() {
        if (!this.mainSubmitButton || this.mainSubmitButton.disabled) return;
        this.mainSubmitButton.click();
      }

      onScrollToTop() {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }
  );
}
